import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BASE_URL_PATH, PHONE_COUNTRIES, STORAGE_CUSTOMER_DATA } from 'src/app/core/constants';
import { RestaurantStore } from '../../../core/stores/restaurant.store';
import { PaymentStore, VerifyProcess } from '../../../core/stores/payment.store';
import { PaymentService } from '../../../core/services/payment.service';
import { Customer, CustomerData, CustomerToken, VerifyState } from '../../../core/models/customer.model';
import { CookieService } from 'ngx-cookie-service';
import { StorageService } from '../../../core/services/storage.service';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UtilService } from 'src/app/core/services/util.service';
import { PhoneFormat } from 'src/app/core/models/order.model';
import { Subject, of } from 'rxjs';
import { catchError, finalize, map, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AlertController, Platform } from '@ionic/angular';
import { RouteService } from 'src/app/core/services/route.service';
import { CustomerStore } from 'src/app/core/stores/customer.store';
import { CustomerService } from 'src/app/core/services/customer.service';
import { OrderStore } from 'src/app/core/stores/order.store';
import { PrivateAlertService } from 'src/app/core/services/private-alert.service';
import { Router } from '@angular/router';
import { parsePhoneNumber, CountryCode } from 'libphonenumber-js';

@Component({
  selector: 'app-verify-phone-number',
  templateUrl: './verify-phone-number.component.html',
  styleUrls: ['./verify-phone-number.component.scss'],
})
export class VerifyPhoneNumberComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() forceTouched: any;
  @Input() isUpdating: boolean;
  @Input() isSharedCardsEnabled: boolean;
  @Input() isNeedSharingPhoneNumber: boolean;
  @Input() isDisableQuickCheckout: boolean;
  @Input() showAuth:boolean = false;
  @Input() dismissModal!: () => void;
  @Input() isPrivate: boolean = false;
  @Input() locationId: string;
  @Output() getPreviousOrders = new EventEmitter();
  @Output() verificationCustomer = new EventEmitter();
  @Output() setCustomerLoyaltyRewards: EventEmitter<Customer> = new EventEmitter<Customer>();
  @Output() saveCustomerData: EventEmitter<string> = new EventEmitter<string>();
  @Output() authoriseCustomer: EventEmitter<string> = new EventEmitter<string>();
  @Output() validChange: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  @Output() isPhoneValid: EventEmitter<boolean> = new EventEmitter<boolean>(true);

  public separateDialCode = false;
  public SearchCountryField = SearchCountryField;
  public PhoneNumberFormat = PhoneNumberFormat;
  public selectedCountry: CountryISO | string = CountryISO.UnitedStates;

  public phoneSubmitted: boolean;
  public phoneValid: boolean;
  public codeFailed: boolean;
  public codeSent: boolean;
  public allowResent: boolean;
  public startSent: boolean;
  public phoneGroup: FormGroup;
  private lastValidPhoneNumber = '';
  private ERROR_MESSAGE = 'Please provide a valid phone number';
  public allowCountries: CountryISO[] = PHONE_COUNTRIES;
  private destroyed$ = new Subject<void>();
  private customer: CustomerData = new CustomerData();
  public isIos:boolean;
  public merchantId: string;
  public isPrivateCustomer: boolean = false;

  public get controls(): {
    [key: string]: AbstractControl;
  } {
    return this.phoneGroup.controls;
  }

  public get allowSendVerifyCode(): boolean {
    return (this.phoneSubmitted && !!this.paymentStore.customer && !this.codeSent) || this.paymentStore.customer?.cards || this.allowResent;
  }

  public get enableVerifyButton(): boolean {
    return (this.verifyButtonText === VerifyState.RESENT_CODE || this.verifyButtonText === VerifyState.SEND_CODE || this.verifyButtonText === VerifyState.VERIFY_PHONE) && !this.startSent;
  }

  public get verifyButtonText(): string {
    if (this.userVerified) {
      return VerifyState.VERIFIED;
    } else if (this.allowResent) {
      return VerifyState.RESENT_CODE;
    } else if (this.codeSent || this.startSent) {
      return VerifyState.CODE_SENT;
    } else if (this.phoneSubmitted) {
      return VerifyState.VERIFY_PHONE;
    }
    return '';
  }

  public get verifyButtonTextStyle(): string {
    if (this.userVerified) {
      return 'phone__input-button-verified';
    } else if ((this.codeSent && !this.allowResent) || (!this.codeSent && !this.phoneSubmitted) || this.startSent) {
      return 'phone__input-button-send';
    } else if (this.phoneSubmitted || this.allowResent) {
      return 'phone__input-button-resent';
    }
    return '';
  }

  public get needVerification(): boolean {
    return !this.paymentStore.verificationCustomer && this.codeSent && !this.startSent;
  }

  public get isActiveCustomerWithCards(): boolean {
    return this.paymentStore.customer && this.paymentStore.customer?.cards && this.paymentStore.customer?.cards.length > 0;
  }

  public get userVerified(): boolean {
    return this.paymentStore.verificationCustomer;
  }

  public get showVerifyButton(): boolean {
    return this.phoneValid || (this.paymentStore.customer?.cards && this.paymentStore.customer?.cards.length > 0) || this.isActiveCustomerWithCards;
  }

  public get error(): AbstractControl {
    return this.phoneGroup.get('phone');
  }

  public get usersPhoneNumber() {
    return this.paymentStore.phoneNumber;
  }

  public set usersPhoneNumber(value: any) {
    this.paymentStore.setPhoneNumber(value.nationalNumber);
    this.updatePhoneNumberValue(value.nationalNumber);
  }

  public errorMessage = this.ERROR_MESSAGE;

  constructor(
    public cookieService: CookieService,
    public paymentService: PaymentService,
    public paymentStore: PaymentStore,
    private storageService: StorageService,
    private restaurantStore: RestaurantStore,
    private formBuilder: FormBuilder,
    private utilService: UtilService,
    private alertController: AlertController,
    public platform: Platform,
    private routeService: RouteService,
    private customerService: CustomerService,
    private orderStore: OrderStore,
    private privateAlert: PrivateAlertService,
    private router: Router,
    private customerStore: CustomerStore
  ) {
    if (this.platform.is('ios')) {
      this.isIos = true;
    }
  }

  ngOnInit() {
    this.setDefaultCountryCode();
    this.initCustomer();
    this.initPhoneForm();
    this.merchantId = this.routeService.getMerchantId();
  }

  private async initCustomer(): Promise<void> {
    const customerStorageData = await this.storageService.get(STORAGE_CUSTOMER_DATA);
    if (customerStorageData) {
      this.customer = customerStorageData;
      const { token, phone } = customerStorageData;
      if (phone && token) {
        this.paymentStore.verifyCustomerState = VerifyProcess.VERIFYING;
        this.paymentStore.setPhoneNumber(phone);
        this.isPhoneValid.emit(true);
        this.controls.phone.setValue(phone);
        this.paymentService
          .getVerifiedCustomerByPhone(phone, token, this.isSharedCardsEnabled, this.isNeedSharingPhoneNumber)
          .subscribe((customer: Customer) => {
            // Normalize phone number to E.164 format
            customer.phone = this.normalizePhoneNumber(customer.phone);

            if(!this.isSharedCardsEnabled && !this.paymentStore.isGroupOrder) {
              this.paymentService.getCustomerById(customer.squareCustomerId, this.isSharedCardsEnabled).subscribe((response)=>{
                const filteredCards = response.cards.filter(card => card.card_brand != "OTHER_BRAND");
                response.cards = filteredCards;
                // Ensure phone is in E.164 format
                response.phone = this.normalizePhoneNumber(response.phone);
                this.paymentStore.customer = response;
                this.paymentStore.verificationCustomer = true;
                this.paymentStore.verifyCustomerState = VerifyProcess.SUCCESS;
                this.lastValidPhoneNumber = response.phone;
                this.phoneValid = true;
                this.phoneSubmitted = true;
                this.saveCustomerInfoToStore();
                this.setCustomerLoyaltyRewards.emit(response);
                this.authoriseCustomer.emit(token);
                this.verificationCustomer.emit();
                this.checkPhone();
              }, () => {
                this.getCustomerByPhone(phone);
              })
            } else {
              this.paymentStore.customer = customer;
              this.paymentStore.verificationCustomer = true;
              this.paymentStore.verifyCustomerState = VerifyProcess.SUCCESS;
              this.lastValidPhoneNumber = customer.phone;
              this.phoneValid = true;
              this.phoneSubmitted = true;
              this.saveCustomerInfoToStore();
              this.setCustomerLoyaltyRewards.emit(customer);
              this.authoriseCustomer.emit(token);
              this.verificationCustomer.emit();
              this.checkPhone();
            }
          }, () => {
            this.getCustomerByPhone(phone);
          });
        return;
      } else if (phone) {
        this.paymentStore.setPhoneNumber(phone);
        this.controls.phone.setValue(phone);
        this.isPhoneValid.emit(true);
        this.lastValidPhoneNumber = phone;
        this.phoneValid = true;
        this.checkPhone();
        this.getCustomerByPhone(phone);
        return;
      }
    }
    this.checkPhone();
  }

  private saveCustomerInfoToStore(): void {
    if (this.paymentStore.customer.givenName) {
      this.paymentStore.setGivenName(this.paymentStore.customer.givenName);
    }
    if (this.paymentStore.customer.familyName) {
      this.paymentStore.setFamilyName(this.paymentStore.customer.familyName);
    }
    if (!this.paymentStore.email) {
      this.paymentStore.setEmail(this.paymentStore.customer.emailAddress);
    }
    if (!this.paymentStore.billingAddress) {
      this.paymentStore.setBillingAddress(this.paymentStore.customer.address);
    }
  }

  public getCustomerByPhone(phone: string, sendVerificationCode: boolean = false): void {
    this.startSent = true;
    this.paymentStore.verificationCustomer = false;
    this.paymentStore.verifyCustomerState = VerifyProcess.VERIFYING;
    this.phoneGroup.disable();
    let isNeedSharingPhoneNumber = this.isNeedSharingPhoneNumber;
    if (this.paymentStore.createTabChecked && this.restaurantStore.group && !this.paymentStore.customer) {
      isNeedSharingPhoneNumber = true;
    }
    this.paymentService.getCustomerByPhone(phone, this.isSharedCardsEnabled, isNeedSharingPhoneNumber).subscribe(
      (customer: Customer) => {
        this.phoneGroup.enable();
        this.startSent = false;
        this.paymentStore.verifyCustomerState = VerifyProcess.SUCCESS;
        // Normalize phone number to E.164 format
        customer.phone = this.normalizePhoneNumber(customer.phone);
        this.paymentStore.customer = customer;
        this.phoneSubmitted = true;
        this.cookieService.set('phoneNumber', customer.phone);
        this.setCustomerLoyaltyRewards.emit(customer);
        this.validChange.emit(true);
        this.checkPhone();
        if (sendVerificationCode) {
          this.verificationCode(customer.orderBotUserId);
        }
        if (!customer.loyaltyRewardsTier || customer.loyaltyRewardsTier?.length === 0) {
          this.paymentStore.selectedLoyaltyReward = null;
          this.paymentStore.useLoyaltyChecked = false;
        }
        if (this.isDisableQuickCheckout) {
          if (this.paymentStore.customer.cards) {
            this.paymentStore.customer.cards = null;
          }
          this.paymentStore.verificationCustomer = true;
          return;
        }
        this.paymentStore.verificationCustomer = false;
      },
      () => {
        this.checkPhone();
        this.phoneGroup.enable();
      }
    );
  }

  public getVerificationCode() {
    if (!this.paymentStore.customer?.orderBotUserId) {
      this.getCustomerByPhone(this.paymentStore.phoneNumber, true);
      return;
    }
    this.verificationCode(this.paymentStore.customer.orderBotUserId);
  }

  public sendVerifyingCode(verificationCode: string) {
    this.codeFailed = false;
    this.allowResent = false;
    this.customerStore.setCustomerLoading(true);
    this.paymentService.sendVerifyingCode(this.paymentStore.customer.orderBotUserId, verificationCode)
      .pipe(
        catchError((error) => {
          console.log("catchError sendVerifying", error);
          this.allowResent = true;
          this.codeFailed = true;
          this.customerStore.setCustomerLoading(false)
          return of(null);
        })
      )
      .subscribe(async (result: CustomerToken) => {
        if (!result) return;
        const prevCustomer = this.paymentStore.customer;
        // Normalize phone number to E.164 format
        result.customer.phone = this.normalizePhoneNumber(result.customer.phone);
        this.paymentStore.customer = result.customer;
        this.paymentStore.customer.loyaltyPointsBalance = prevCustomer.loyaltyPointsBalance;
        this.paymentStore.customer.loyaltyAccountId = prevCustomer.loyaltyAccountId;
        this.paymentStore.customer.loyaltyRewardsTier = prevCustomer.loyaltyRewardsTier;
        this.codeSent = false;
        this.allowResent = false;
        this.fillPersonalInfo(this.paymentStore.customer, result.customer);
        this.saveCustomerInfoToStore();
        await this.storageService.set(
          STORAGE_CUSTOMER_DATA,
          new CustomerData(
            result.token,
            this.paymentStore.customer.phone,
            this.paymentStore.customer.orderBotUserId
          )
        );
        this.paymentStore.verificationCustomer = true;
        this.saveCustomerData.emit(result.token);
        this.getPreviousOrders.emit();
        this.verificationCustomer.emit();
        this.customerStore.setCustomerLoading(false)
        this.paymentService.getVerifiedCustomerByPhone(
          result.customer.phone,
          result.token,
          this.isSharedCardsEnabled,
          this.isNeedSharingPhoneNumber
        ).pipe(
          tap((customer) => {
            if (customer) {
              // Normalize phone number to E.164 format
              customer.phone = this.normalizePhoneNumber(customer.phone);
              this.paymentStore.customer = customer;
            }
          }),
          switchMap(() => {
            if (!this.isPrivate) return of(true); 
  
            return this.customerService.isPrivateGroup(
              this.merchantId,
              this.paymentStore.customer.squareCustomerId
            ).pipe(
              switchMap((isPrivateCustomer: boolean) => {
                  this.isPrivateCustomer = isPrivateCustomer;
                  return of(isPrivateCustomer);
               
              })
            );
          }),
          finalize(() => {
            if (this.dismissModal && typeof this.dismissModal === 'function') {
              if (!this.isPrivate || this.isPrivateCustomer) {
                this.dismissModal();
              } else {
                this.showPrivateAppAlert();
              }
            }
          })
        ).subscribe();
      });
  }
  

  public fillPersonalInfo(customer: Customer, verifiedCustomer: Customer) {
    customer.givenName = verifiedCustomer.givenName;
    customer.familyName = verifiedCustomer.familyName;
    customer.emailAddress = verifiedCustomer.emailAddress;
    customer.address = verifiedCustomer.address;
  }

  private async showPrivateAppAlert() {
    this.privateAlert.showPrivateAppAlert(
      () => {
        this.dismissModal();
        this.navigateToRestaurant();
      }
    );
  }

  public async navigateToRestaurant() : Promise<boolean> {
      if (this.locationId) {
        this.storageService.set('currentRestaurantId', this.locationId);
        const baseUrl = this.routeService.getBaseUrl();
        return this.router.navigate([baseUrl, 'locations', this.locationId]);
      }
      return this.router.navigate([BASE_URL_PATH, this.merchantId]);
  }

  private async navigateToSubscriptions() {
    this.utilService.navigateToSubscriptions();
  }

  private updatePhoneNumberValue(value: string): void {
    this.phoneValid = false;
    this.allowResent = false;
    if (this.controls.phone.valid && value !== this.lastValidPhoneNumber) {
      this.lastValidPhoneNumber = value;
      this.codeSent = false;
      this.phoneValid = true;
      this.getCustomerByPhone(this.usersPhoneNumber);
    } else if (value === this.lastValidPhoneNumber) {
      this.phoneValid = true;
      this.phoneSubmitted = true;
      this.paymentStore.customer.phone = this.lastValidPhoneNumber;
      this.storageService.set(
        STORAGE_CUSTOMER_DATA,
        new CustomerData(
          null,
          this.paymentStore.customer.phone,
          this.paymentStore.customer.orderBotUserId
        )
      );
    }
  }

  private initPhoneForm(): void {
    this.phoneGroup = this.formBuilder.group({
      phone: new FormControl(undefined, [Validators.required]),
    });
  }

  private checkPhone(): void {
    this.controls.phone.valueChanges.subscribe((value: PhoneFormat) => {
      if (this.controls.phone.valid && value) {
        this.paymentStore.setPhoneNumber(value.e164Number);
        this.updatePhoneNumberValue(value.e164Number);
        this.isPhoneValid.emit(true);
        return;
      }
      this.isPhoneValid.emit(false);
      this.allowResent = false;
      this.phoneSubmitted = false;
    });
  }

  private setDefaultCountryCode(): void {
    this.restaurantStore.restaurantAvailableStatus$.subscribe((restaurant) => {
      if (restaurant?.country) {
        this.selectedCountry = restaurant.country;
        return;
      }
      this.selectedCountry = CountryISO.UnitedStates;
    });
  }

  public allowResendCode(): void {
    this.paymentStore.verificationCustomer = false;
    this.forceTouched = false;
    this.codeSent = false;
    this.phoneValid = true;
    this.phoneSubmitted = true;
    this.startSent = false;
    this.getVerificationCode();
  }

  private verificationCode(botUserId: string): void {
    if (this.allowSendVerifyCode) {
      this.startSent = true;
      this.allowResent = false;
      this.paymentService.getVerifyingCode(botUserId).subscribe(
        () => {
          this.codeSent = true;
          this.startSent = false;
          this.allowResent = false;
          setTimeout(() => (this.allowResent = true), 3000);
        },
        () => {
          this.startSent = false;
        }
      );
    }
  }

  public async receiveAuth(response:{isAuth: boolean, customerToken:{customer:Customer,token:string}}) {
    const {isAuth, customerToken } = response;
    if(isAuth){
      // Normalize phone number to E.164 format
      if (customerToken.customer && customerToken.customer.phone) {
        customerToken.customer.phone = this.normalizePhoneNumber(customerToken.customer.phone);
      }
      const normalizedPhone = this.normalizePhoneNumber(this.paymentStore.customer.phone);
      this.lastValidPhoneNumber = normalizedPhone;
      this.customer = {
        token:customerToken.token,
        phone:normalizedPhone,
        orderBotUserId:this.paymentStore.customer.orderBotUserId
      };
      this.utilService.dismissActiveLoader();
      if (this.dismissModal && typeof this.dismissModal === 'function') {
        this.dismissModal();
      }
    } else {
      //need to get phonenumber
      this.utilService.dismissActiveLoader();
      this.showAuth = false;
      await this.showPhoneNumberAlert();
    }
  }

  private async showPhoneNumberAlert() {
    const alert = await this.alertController.create({
      header: 'Sign In Required',
      message: 'Please continue to sign in with your phone number.',
      buttons: ['OK']
    });
  
    await alert.present();
  }

  /**
   * Normalizes phone number to E.164 format  using the selected country
   * Handles various input formats: 
   * @param phone - Phone number in any format
   * @returns Phone number in E.164 format with correct country code
   */
  private normalizePhoneNumber(phone: string): string {
    if (!phone) return phone;

    try {
      // Get the country code from selectedCountry (e.g., 'US', 'CA', 'MX')
      const countryCode = (typeof this.selectedCountry === 'string'
        ? this.selectedCountry
        : this.selectedCountry) as CountryCode;

      // Parse the phone number with the selected country as default
      const phoneNumber = parsePhoneNumber(phone, countryCode);

      // Return in E.164 format if valid
      if (phoneNumber && phoneNumber.isValid()) {
        return phoneNumber.format('E.164');
      }
    } catch (error) {
      console.warn('Error normalizing phone number:', error);
    }

    // If parsing fails, return original
    return phone;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }
}
